<template>
  <section class="py-12 px-4 bg-white dark:bg-stone-800 border-t border-stone-200 dark:border-stone-700">
    <div class="max-w-4xl mx-auto font-quicksand text-stone-800 dark:text-stone-200">

      <article class="prose dark:prose-invert max-w-none">
        <h2 class="text-3xl font-bold mb-6 text-center text-primary-600 dark:text-primary-400">
          The Ultimate Random Name Picker Wheel
        </h2>

        <div class="mb-8 leading-relaxed">
          <p class="mb-4">
            Welcome to <strong>Spin the Names</strong>, the most versatile and easy-to-use random name picker on the
            web.
            Whether you are a teacher looking for a fair way to pick students, a raffle organizer selecting a winner,
            or simply a group of friends trying to decide where to eat dinner, our spinning wheel is designed to make
            selection fun, fair, and transparent.
          </p>
          <p class="mb-4">
            Unlike simple random number generators, Spin the Names provides a visual and auditory experience that builds
            anticipation. The satisfying spin, the ticking sound, and the celebratory confetti effect make every
            decision
            an event in itself. Best of all, it is completely free, operates directly in your browser, and requires
            no downloads or installation.
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-8 mb-12">
          <section>
            <h3 class="text-2xl font-bold mb-4 text-primary-600 dark:text-primary-400">
              <i class="fas fa-graduation-cap mr-2"></i>Perfect for Teachers
            </h3>
            <p class="mb-4">
              Classroom management is easier when students know that participation is fair. Use the wheel to:
            </p>
            <ul class="list-disc pl-5 space-y-2 mb-4">
              <li>Select students for answering questions ("Cold Calling").</li>
              <li>Assign classroom presentation orders randomly.</li>
              <li>Form groups or partners for projects.</li>
              <li>Distribute prizes or classroom rewards.</li>
            </ul>
            <router-link to="/use-cases/teachers" class="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold hover:underline">
              Learn more about using it in the classroom <i class="fas fa-arrow-right ml-2 text-sm"></i>
            </router-link>
          </section>

          <section>
            <h3 class="text-2xl font-bold mb-4 text-primary-600 dark:text-primary-400">
              <i class="fas fa-gift mr-2"></i>Events & Raffles
            </h3>
            <p class="mb-4">
              Hosting a giveaway? The "Spin the Names" wheel adds a layer of excitement that a spreadsheet calculation
              simply can't match.
            </p>
            <ul class="list-disc pl-5 space-y-2 mb-4">
              <li>Stream the wheel live during webinars or contests.</li>
              <li>Pick winners for Instagram or Facebook giveaways.</li>
              <li>Use it at office parties for Secret Santa assignments.</li>
              <li>Determine draft orders for fantasy sports leagues.</li>
            </ul>
            <router-link to="/use-cases/events" class="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold hover:underline">
              Learn more about running giveaways <i class="fas fa-arrow-right ml-2 text-sm"></i>
            </router-link>
          </section>

          <section>
            <h3 class="text-2xl font-bold mb-4 text-primary-600 dark:text-primary-400">
              <i class="fas fa-broadcast-tower mr-2"></i>Streamers & Content Creators
            </h3>
            <p class="mb-4">
              Make your community feel involved. Use the wheel live on stream to pick giveaway winners, choose
              challengers, or let chat decide your next move.
            </p>
            <ul class="list-disc pl-5 space-y-2 mb-4">
              <li>Pick subscriber or follower giveaway winners transparently.</li>
              <li>Choose who gets to join your game session.</li>
              <li>Let the wheel decide your next challenge or game mode.</li>
              <li>Build hype with a live spin chat can watch in real time.</li>
            </ul>
            <router-link to="/use-cases/streamers" class="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold hover:underline">
              Learn more about using it on stream <i class="fas fa-arrow-right ml-2 text-sm"></i>
            </router-link>
          </section>

          <section>
            <h3 class="text-2xl font-bold mb-4 text-primary-600 dark:text-primary-400">
              <i class="fas fa-briefcase mr-2"></i>Teams & Business
            </h3>
            <p class="mb-4">
              Remove bias and politics from everyday team decisions. A transparent random wheel everyone can see makes
              the outcome undeniable.
            </p>
            <ul class="list-disc pl-5 space-y-2 mb-4">
              <li>Pick who kicks off the standup or presents first.</li>
              <li>Assign tasks or responsibilities fairly.</li>
              <li>Form random project teams across departments.</li>
              <li>Settle low-stakes office decisions without debate.</li>
            </ul>
            <router-link to="/use-cases/business" class="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold hover:underline">
              Learn more about using it at work <i class="fas fa-arrow-right ml-2 text-sm"></i>
            </router-link>
          </section>
        </div>

        <section class="mb-12">
          <h2 class="text-3xl font-bold mb-6 text-center text-primary-600 dark:text-primary-400">
            Features & Capabilities
          </h2>
          <p class="mb-6 text-center">
            We've built Spin the Names with powerful features that go beyond basic randomization.
          </p>

          <div class="space-y-6">
            <div class="bg-stone-50 dark:bg-stone-900 p-6 rounded-lg shadow-sm">
              <h4 class="text-xl font-bold mb-2">🎨 Fully Customizable</h4>
              <p>
                Make the wheel your own! You can edit the title and description to fit your event.
                Add up to 1,000 names, words, or numbers. The wheel automatically adjusts its layout
                to handle small or large lists with ease.
              </p>
            </div>

            <div class="bg-stone-50 dark:bg-stone-900 p-6 rounded-lg shadow-sm">
              <h4 class="text-xl font-bold mb-2">💾 Save & Share</h4>
              <p>
                Don't want to re-type your list every time? Create a free account to save your wheels.
                You can also generate unique sharing links. Send a link to colleagues, or share a
                "View Only" version so others can spin the wheel you created without modifying it.
              </p>
            </div>

            <div class="bg-stone-50 dark:bg-stone-900 p-6 rounded-lg shadow-sm">
              <h4 class="text-xl font-bold mb-2">🔒 Privacy Focused</h4>
              <p>
                We take your privacy seriously. Your data is secure, and we do not sell your information.
                All processing of the random selection logic happens right in your secure browser session.
                For more details, please review our Privacy Policy.
              </p>
            </div>
          </div>
        </section>

        <section class="mb-8">
          <h2 class="text-3xl font-bold mb-6 text-center text-primary-600 dark:text-primary-400">
            Frequently Asked Questions
          </h2>
          <div class="space-y-4">
            <details class="group">
              <summary
                class="flex justify-between items-center font-medium cursor-pointer list-none p-4 bg-stone-100 dark:bg-stone-700 rounded-lg">
                <span>Is the wheel truly random?</span>
                <span class="transition group-open:rotate-180">
                  <i class="fas fa-chevron-down"></i>
                </span>
              </summary>
              <div class="text-stone-600 dark:text-stone-300 mt-3 px-4">
                Yes! We use cryptographically strong pseudo-random number generators standard in modern web browsers
                to ensure that every spin is unpredictable and fair. The result is calculated the moment you click
                "Spin".
              </div>
            </details>

            <details class="group">
              <summary
                class="flex justify-between items-center font-medium cursor-pointer list-none p-4 bg-stone-100 dark:bg-stone-700 rounded-lg">
                <span>Can I use images instead of text?</span>
                <span class="transition group-open:rotate-180">
                  <i class="fas fa-chevron-down"></i>
                </span>
              </summary>
              <div class="text-stone-600 dark:text-stone-300 mt-3 px-4">
                Currently, Spin the Names is optimized for text-based entries to ensure fast loading and clean
                readability.
                However, we are exploring image support for future updates!
              </div>
            </details>

            <details class="group">
              <summary
                class="flex justify-between items-center font-medium cursor-pointer list-none p-4 bg-stone-100 dark:bg-stone-700 rounded-lg">
                <span>How do I remove a name after it has been picked?</span>
                <span class="transition group-open:rotate-180">
                  <i class="fas fa-chevron-down"></i>
                </span>
              </summary>
              <div class="text-stone-600 dark:text-stone-300 mt-3 px-4">
                After a winner is selected, a popup dialog will appear celebrating the result.
                This dialog includes a "Remove" button. Clicking this will temporarily take that name off the wheel
                so it cannot be picked again during the current session. You can always reload the page to bring
                everyone back.
              </div>
            </details>
          </div>
        </section>

      </article>
    </div>
  </section>
</template>

<script setup>
// No script logic needed for static content
</script>
